// Helpers for SmartForm / SmartFormReply
// there are also some use-case specific things in other places - like in company_todos


import {
    FormatDateHelper,
    //FormatTimeFromIso,
    toNumberOrNull,
    FormatDecimalHelper,
    //FormatPercentHelper,
    //FormatCurrencyHelper,
    //DurationSecondsToHumanized,
} from '../../js/filters-helpers';
import cloneDeep from 'lodash/cloneDeep';
// import parsePhoneNumber from 'libphonenumber-js/min';


const smartFormFieldTypes = [
    {
        slug: 'short_text',
        name: 'Short Text',
        valueStringFormatter: (value) => {
            return value;
        },
    },
    {
        slug: 'rich_text',
        name: 'Rich Text',
        valueStringFormatter: (value) => {
            return value;
        },
    },
    {
        slug: 'single_select',
        name: 'Single Select',
        valueStringFormatter: (value, fieldObj) => {
            let choices;
            const choicesRaw = fieldObj.extra?.choices ?? [];
            if (Array.isArray(choicesRaw) && choicesRaw.length) {
                const firstChoice = choicesRaw[0];
                if (typeof firstChoice === 'string') {
                    choices = choicesRaw.map(c => ({value: c, label: c}));
                } else if ('label' in choicesRaw[0] && 'value' in choicesRaw[0]) {
                    choices = cloneDeep(choicesRaw);
                } else {
                    throw new Error(`Trying to get choices for field ${fieldObj.label} but cannot figure out the type`);
                }
            } else {
                choices = [];  // ???
            }
            const choiceMap = Object.fromEntries(choices.map(choiceObj => ([choiceObj.value, choiceObj.label])));
            return choiceMap[value] ?? value;
        },
    },
    {
        slug: 'email_address',
        name: 'Email Address',
        valueStringFormatter: (value) => {
            return value;
        },
    },
    {
        slug: 'integer',
        name: 'Integer',
        valueStringFormatter: (value) => {
            return String(value);
        },
    },
    {
        slug: 'float',  // encourage use of decimal instead?
        name: 'Number',  
        valueStringFormatter: (value, fieldObj) => {
            // try to get 'decimal_places' from fieldObj.extra
            const decimalPlaces = fieldObj.extra?.decimal_places ?? fieldObj.decimal_places;
            // ok if its still undefined... 
            // coerce the value to number, or null so we know what we're working with
            const numOrNull = toNumberOrNull(value);
            return numOrNull === null ? '' : FormatDecimalHelper(numOrNull, decimalPlaces);
        },
    },
    {
        slug: 'decimal',
        name: 'Decimal Number',
        valueStringFormatter: (value, fieldObj) => {
            // try to get 'decimal_places' from fieldObj.extra
            const decimalPlaces = fieldObj.extra?.decimal_places ?? fieldObj.decimal_places;
            // ok if its still undefined... 
            // coerce the value to number, or null so we know what we're working with
            const numOrNull = toNumberOrNull(value);
            return numOrNull === null ? '' : FormatDecimalHelper(numOrNull, decimalPlaces);
        },
    },
    {
        slug: 'money_fixed_currency',
        name: 'Money (single currency)',
        valueStringFormatter: (value, fieldObj) => {
            // need 3-letter ISO currency code -- gotta have it
            // likely at fieldObj.extra.currency_code but we check other places if not there
            const currencyCode = fieldObj.extra?.currency_code || fieldObj.currency_code;
            // also need to get 'decimal_places' from fieldObj.extra
            const decimalPlaces = fieldObj.extra?.decimal_places ?? fieldObj.decimal_places;
            // ok if its still undefined...  will just end up using default for that currency
            // coerce the value to number, or null so we know what we're working with
            const numOrNull = toNumberOrNull(value);
            if (numOrNull === null || !currencyCode) {
                return '';  // we're making strings here, so return empty string if you got nothin'
            }
            // now use Intl.NumberFormat to format it as a currency with specified digits
            // we'll hard-code en-US for now, because I am a typical small-minded American ;) (and because most global manufacturers are used to US / USD currency formats)
            const formatOpts = {
                style: 'currency',
                currency: currencyCode,
                maximumFractionDigits: decimalPlaces,
            };
            return new Intl.NumberFormat('en-US', formatOpts).format(value);
            //return numOrNull === null ? '' : FormatCurrencyHelper(numOrNull, currencyCode);  // this always returns a string
        },
    },
    {
        slug: 'boolean',
        name: 'True/False',
        valueStringFormatter: (value, fieldObj) => {
            // can optionally have 'true_label' and 'false_label' in the fieldObj.extra
            // if not a known true or known false, we just return String(value)
            const trueLabel = fieldObj.extra?.true_label || 'Yes';
            const falseLabel = fieldObj.extra?.false_label || 'No';
            const knownTrueValues = [ 
                'true', 'True', 'on', 'ON', true, 1,
                'yes', 'YES', 'Yes', 'y',
            ];
            const knownFalseValues = [
                'false', 'False', 'off', 'OFF', false, 0,
                'no', 'NO', 'No', 'n',
                // include null and undefined here???  or should that mean error?
            ];
            const isKnownTrue = knownTrueValues.includes(value);
            const isKnownFalse = knownFalseValues.includes(value);
            return isKnownTrue ? trueLabel : (isKnownFalse ? falseLabel : String(value));
        },
    },
    {
        slug: 'date',
        name: 'Date',
        valueStringFormatter: (value) => {
            return FormatDateHelper(value);
        },
    },
    {
        slug: 'nested_form',
        name: 'Nested Form',
        valueStringFormatter: (value) => {  // fieldObj is also passed in
            // use something similar/same as single_select?
            return value;  // just this for now
        },
    },
    {
        slug: 'grid_row',
        name: 'Grid Row',
        valueStringFormatter: () => {  // (value, fieldObj) is also passed in
            // the 'formatted' value should just be empty here... so we don't show JS objects anywhere
            return '';  // just this for now
        },
    },
    {
        slug: 'data_grid',
        name: 'Data Grid',
        valueStringFormatter: () => {  // (value, fieldObj) is also passed in
            // the 'formatted' value should just be empty here... so we don't show JS objects anywhere
            return '';
        },
    },
];

// mapping keyed by 'slug'
const smartFormFieldTypeMap = Object.fromEntries(smartFormFieldTypes.map(ft => ([ft.slug, ft])));

// LATER - make another set of formatters that formats as HTML?  download buttons for files, <a> tags for URLs, etc.

const formatSmartFormValue = (value, fieldObj) => {
    // format some value from a SmartForm (SmartFormReply) as a STRING to show on front-end
    // ARGS:
    //  - value - the value string or object for this field
    //  - fieldObj - the serialized SmartFormField object
    //      should have .type_slug attribute (from backend)
    //      needed for choices, currency, decimal places, etc.
    const fieldType = smartFormFieldTypeMap[fieldObj.type_slug];
    // if we do not have fieldType, just output the value -- maybe you added new types... or maybe layout types
    return fieldType ? fieldType.valueStringFormatter(value, fieldObj) : `${value} (${fieldObj.type_slug})`;
}



export {
    formatSmartFormValue,
}